import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Popper
} from '@mui/material';
import { styled } from '@mui/system';
import MuiDownloadIcon from '@mui/icons-material/Download';
import MuiVisibilityIcon from '@mui/icons-material/Visibility';
import MuiCloseIcon from '@mui/icons-material/Close';
import MuiPrintIcon from '@mui/icons-material/Print';
import MuiPictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MuiTableChartIcon from '@mui/icons-material/TableChart';
import CustomYearDatePicker from '../../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import ReportsService from '../../../../Services/reports.service';
import DocumentService from '../../../../Services/documents.service';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';
import Constants from '../../../../assests/Constants/constants';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import { useHamburgerMenu } from '../../../../Contexts/HamburgerMenuContext.js';
import TransactionSearchResultModal from '../../../Widgets/TransactionSearchResultModal/TransactionSearchResultModal';

const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 'var(--Spacing-md, 8px)',
    background: 'var(--white, #FFF)',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
  },
  '& .MuiAutocomplete-listbox': {
    padding: theme.spacing(1),
  },
  '& .MuiAutocomplete-option': {
    borderRadius: 'var(--Spacing-sm, 4px)',
    '&[aria-selected="true"], &.Mui-focused, &:hover': {
      background: 'var(--orange, #F9AA2A) !important',
    },
  },
}));

const ReferenceSequenceViewModal = ({
  open,
  onClose,
  direction,
  t,
  startYearDateForReference,
  handleStartReferenceYearDate,
  dayjs,
  formattedAccountCardOptions,
  accountCardOptions,
  selectedFormattedAccountCard,
  handleFormattedAccountCardChange,
  AutocompleteSelect,
  client,
  token,
  user
}) => {
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [startYear, setStartYear] = useState(dayjs());
  const [endYear, setEndYear] = useState(dayjs());

  const getReferenceSequenceDateRange = () => {
    let start = dayjs(startYear).startOf('year');
    let end = dayjs(endYear).endOf('year');
    if (end.isBefore(start)) {
      const swappedStart = dayjs(endYear).startOf('year');
      const swappedEnd = dayjs(startYear).endOf('year');
      start = swappedStart;
      end = swappedEnd;
    }
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const getReferenceSequenceYearLabel = () => {
    const startY = dayjs(startYear).year();
    const endY = dayjs(endYear).year();
    return startY === endY ? `${startY}` : `${startY}-${endY}`;
  };
  const [sortCodeOptions, setSortCodeOptions] = useState([]);
  const [selectedSortCode, setSelectedSortCode] = useState(null);
  
  // Use HamburgerMenuContext for global EditModal
  const {
    selectedDocumentId,
    selectedSectionKey,
    isJustEditModalOpen,
    handleDocumentRowClick,
    setSelectedDocumentId,
    setSelectedSectionKey,
    setIsJustEditModalOpen,
    editModalUpdated,
    setEditModalUpdated,
  } = useHamburgerMenu();
  
  // Tab state management
  const [activeTab, setActiveTab] = useState('all');
  const [tabContent, setTabContent] = useState({
    all: '',
    taxInvoices: '',
    creditInvoices: '',
    invoiceReceipts: ''
  });


  
  // Fetch sort codes when modal opens
  // Fetch sort codes when modal opens
  useEffect(() => {
    const fetchSortCodes = async () => {
      if (open && client && client.accountantId && token) {
        try {
          const response = await EnumsService.GetSortCodesByAccountantId(client.accountantId, token);
          if (response.ok) {
            const sortCodes = await response.json();
            if (sortCodes && sortCodes.length > 0) {
              // Map sort codes to the format expected by AutocompleteSelect
              const options = sortCodes.map((sortCode) => ({
                value: sortCode.sortCodeId,
                label: `${sortCode.sortCodeId} - ${sortCode.sortCodeName}`,
              }));
              setSortCodeOptions(options);
            } else {
              setSortCodeOptions([]);
            }
          } else {
            console.error('Failed to fetch sort codes');
            setSortCodeOptions([]);
          }
        } catch (error) {
          console.error('Error fetching sort codes:', error);
          setSortCodeOptions([]);
        }
      }
    };

    fetchSortCodes();
  }, [open, client?.accountantId, token]);

  // Refresh report when EditModal is updated
  useEffect(() => {
    if (editModalUpdated && showReport) {
      handleViewReport();
      setEditModalUpdated(false);
    }
  }, [editModalUpdated, showReport]);
  
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [printing, setPrinting] = useState(false);


 

 


  // Function to parse HTML content and separate into tabs
  const parseHtmlIntoTabs = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract the header and stats
    const header = doc.querySelector('.report-header');
    const reportInfo = doc.querySelector('.report-info');
    
    // Find all section headers and their corresponding tables
    const sections = [];
    const sectionHeaders = doc.querySelectorAll('.section-header');
    
    sectionHeaders.forEach(header => {
      const headerText = header.textContent.trim();
      const nextTable = header.nextElementSibling;
      
      if (nextTable && nextTable.tagName === 'TABLE') {
        sections.push({
          header: headerText,
          content: nextTable.outerHTML
        });
      }
    });
    
    // Create tab content
    const tabContent = {
      all: html, // Full HTML for "All" tab
      taxInvoices: '',
      creditInvoices: '',
      invoiceReceipts: ''
    };
    
    // Separate content by section type
    sections.forEach(section => {
      if (section.header.includes('חשבונית מס') && !section.header.includes('זיכוי') && !section.header.includes('קבלה')) {
        tabContent.taxInvoices = createTabHtml(header, reportInfo, section.content, section.header);
      } else if (section.header.includes('חשבונית מס זיכוי')) {
        tabContent.creditInvoices = createTabHtml(header, reportInfo, section.content, section.header);
      } else if (section.header.includes('חשבונית מס קבלה')) {
        tabContent.invoiceReceipts = createTabHtml(header, reportInfo, section.content, section.header);
      }
    });
    
    return tabContent;
  };
  
  // Helper function to create HTML for individual tabs
  const createTabHtml = (header, reportInfo, tableContent, sectionHeader) => {
    return `
      <div class="report-container">
        ${header ? header.outerHTML : ''}
        ${reportInfo ? reportInfo.outerHTML : ''}
        <div class="table-container">
          <div class="section-header" style="background: #e3f2fd; padding: 10px; margin: 10px 0; border-radius: 5px; text-align: center; font-weight: bold; font-size: 16px;">
            ${sectionHeader}
          </div>
          ${tableContent}
        </div>
      </div>
    `;
  };

  const handleViewReport = async () => {
    setLoading(true);
    setError('');
    setHtmlContent('');
    setShowReport(false);
    setActiveTab('all');

    try {
      const accountCardCode = selectedFormattedAccountCard
        ? selectedFormattedAccountCard.value
        : null;

      const sortCodeId = selectedSortCode ? selectedSortCode.value : null;

      const { startDate, endDate } = getReferenceSequenceDateRange();

      const html = await ReportsService.getReferenceSequenceReportHtml(
        client.caseId,
        startDate,
        endDate,
        accountCardCode,
        sortCodeId,
        token
      );

      
      // Parse HTML and separate into tabs
      const parsedContent = parseHtmlIntoTabs(html);
      setTabContent(parsedContent);
      setHtmlContent(html);
      setShowReport(true);
    } catch (error) {
      console.error(error.message);
      if (error.message && error.message.includes('לקרטיס וקוד מיון')) {
        const correctedMessage = error.message.replace(/לקרטיס/g, 'לכרטיס');
        setError(t('reportDownloadFailure') + ': ' + correctedMessage);
      } else {
        setError(t('reportDownloadFailure'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const accountCardCode = selectedFormattedAccountCard
        ? selectedFormattedAccountCard.value
        : null;

      const sortCodeId = selectedSortCode ? selectedSortCode.value : null;

      const { startDate, endDate } = getReferenceSequenceDateRange();

      await ReportsService.getReferenceSequenceReport(
        client.caseId,
        startDate,
        endDate,
        accountCardCode,
        sortCodeId,
        token
      );
    } catch (error) {
      console.error(error.message);
      if (error.message && error.message.includes('לקרטיס וקוד מיון')) {
        const correctedMessage = error.message.replace(/לקרטיס/g, 'לכרטיס');
        setError(t('reportDownloadFailure') + ': ' + correctedMessage);
      } else {
        setError(t('reportDownloadFailure'));
      }
    }
  };

  const handlePrintReport = () => {
    setPrinting(true);
    
    try {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        // Use the current tab content for printing, fallback to full HTML content
        let contentToPrint = '';
        
        if (activeTab === 'all') {
          contentToPrint = htmlContent;
        } else if (tabContent[activeTab] && tabContent[activeTab].trim() !== '') {
          contentToPrint = tabContent[activeTab];
        } else {
          contentToPrint = htmlContent;
        }
        
        // Ensure we have content to print
        if (!contentToPrint || contentToPrint.trim() === '') {
          console.error('No content available for printing');
          printWindow.close();
          toast.error(t('NoContentToPrint'));
          return;
        }
        
        printWindow.document.write(contentToPrint);
        printWindow.document.close();
        printWindow.focus();
        
        // Wait a bit for the content to load before printing
        setTimeout(() => {
          printWindow.print();
          setPrinting(false);
          // Don't close immediately, let user decide
          // printWindow.close();
        }, 500);
      } else {
        toast.error(t('PrintWindowBlocked'));
        setPrinting(false);
      }
    } catch (error) {
      console.error('Error opening print window:', error);
      toast.error(t('PrintError'));
      setPrinting(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const accountCardCode = selectedFormattedAccountCard
        ? selectedFormattedAccountCard.value
        : null;

      const sortCodeId = selectedSortCode ? selectedSortCode.value : null;

      // Call the backend to generate PDF - service already returns a Blob
      const { startDate, endDate } = getReferenceSequenceDateRange();

      const blob = await ReportsService.getReferenceSequenceReportPdf(
        client.caseId,
        startDate,
        endDate,
        accountCardCode,
        sortCodeId,
        token
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ReferenceSequenceReport_${client.caseId}_${getReferenceSequenceYearLabel()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('PdfDownloadedSuccessfully'));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('ErrorDownloadingPdf'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const accountCardCode = selectedFormattedAccountCard
        ? selectedFormattedAccountCard.value
        : null;

      const sortCodeId = selectedSortCode ? selectedSortCode.value : null;

      // Call the backend to generate Excel - service already returns a Blob
      const { startDate, endDate } = getReferenceSequenceDateRange();

      const blob = await ReportsService.getReferenceSequenceReportExcel(
        client.caseId,
        startDate,
        endDate,
        accountCardCode,
        sortCodeId,
        token
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ReferenceSequenceReport_${client.caseId}_${getReferenceSequenceYearLabel()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('ExcelDownloadedSuccessfully'));
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error(t('ErrorDownloadingExcel'));
    } finally {
      setDownloadingExcel(false);
    }
  };


  const handleIncomeClick = async (incomeId, documentType = 'Incomes') => {
    if (!incomeId) {
      console.warn('Missing document ID for click');
      return;
    }

    const sectionKey = documentType === 'JournalEntries' ? 'JournalEntries' : 'Incomes';

    // Use the global handleDocumentRowClick from HamburgerMenuContext
    if (handleDocumentRowClick) {
      handleDocumentRowClick(incomeId, sectionKey);
    } else {
      console.error('handleDocumentRowClick is not available from HamburgerMenuContext');
      toast.error(t('EditModalNotAvailable'));
    }
  };


  const handleClose = () => {
    setHtmlContent('');
    setShowReport(false);
    setError('');
    setActiveTab('all');
    setSelectedSortCode(null);
    setTabContent({
      all: '',
      taxInvoices: '',
      creditInvoices: '',
      invoiceReceipts: ''
    });
    onClose();
  };

  const handleCloseReport = () => {
    setHtmlContent('');
    setShowReport(false);
    setError('');
    setActiveTab('all');
    setSelectedSortCode(null);
    setTabContent({
      all: '',
      taxInvoices: '',
      creditInvoices: '',
      invoiceReceipts: ''
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="reference-sequence-view-modal-title"
        aria-describedby="reference-sequence-view-modal-description"
        sx={{ zIndex: 1000 }}
        disableEnforceFocus 
        disableAutoFocus
      >
        <Box className={`reference-sequence-view-modal ${direction}`} sx={{
          position: 'relative',
          width: showReport ? '300mm' : 'auto',
          height: showReport ? '95vh' : 'auto',
          maxWidth: showReport ? '300mm' : '500px',
          maxHeight: showReport ? '90%' : '600px',
          margin: 'auto',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          overflow: 'hidden',
          top: '50%',
          transform: 'translateY(-50%)',
          '@media (max-width: 220mm)': {
            width: showReport ? '95vw' : 'auto',
            maxWidth: showReport ? '95vw' : '500px'
          }
        }}>
          {showReport ? (
            <>
              {/* Header with close and download buttons */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px',
                borderBottom: '1px solid #ccc',
                paddingBottom: '10px'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('ReferenceSequenceReport')}
                </Typography>
                <Box>
                  <IconButton
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf || downloadingExcel}
                    sx={{ 
                      marginRight: 1,
                      color: downloadingPdf ? '#ffcdd2' : '#d32f2f',
                      '&:hover': {
                        backgroundColor: downloadingPdf ? 'transparent' : '#ffebee',
                        color: downloadingPdf ? '#ffcdd2' : '#b71c1c'
                      },
                      '&:disabled': {
                        color: '#ffcdd2'
                      }
                    }}
                    title={downloadingPdf ? t('DownloadingPDF') : t('DownloadPDF')}
                  >
                    {downloadingPdf ? (
                      <CircularProgress size={20} sx={{ color: '#d32f2f' }} />
                    ) : (
                      <MuiPictureAsPdfIcon />
                    )}
                  </IconButton>

                  <IconButton
                    onClick={handleDownloadExcel}
                    disabled={downloadingPdf || downloadingExcel}
                    sx={{ 
                      marginRight: 1,
                      color: downloadingExcel ? '#c8e6c9' : '#2e7d32',
                      '&:hover': {
                        backgroundColor: downloadingExcel ? 'transparent' : '#e8f5e8',
                        color: downloadingExcel ? '#c8e6c9' : '#1b5e20'
                      },
                      '&:disabled': {
                        color: '#c8e6c9'
                      }
                    }}
                    title={downloadingExcel ? t('DownloadingExcel') : t('DownloadExcel')}
                  >
                    {downloadingExcel ? (
                      <CircularProgress size={20} sx={{ color: '#2e7d32' }} />
                    ) : (
                      <MuiTableChartIcon />
                    )}
                  </IconButton>

                  <IconButton
                    onClick={handlePrintReport}
                    disabled={printing || downloadingPdf || downloadingExcel}
                    sx={{ 
                      marginRight: 1,
                      color: printing ? '#bbdefb' : '#1976d2',
                      '&:hover': {
                        backgroundColor: printing ? 'transparent' : '#e3f2fd',
                        color: printing ? '#bbdefb' : '#0d47a1'
                      },
                      '&:disabled': {
                        color: '#bbdefb'
                      }
                    }}
                    title={printing ? t('Printing') : t('Print')}
                  >
                    {printing ? (
                      <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                    ) : (
                      <MuiPrintIcon />
                    )}
                  </IconButton>

                  <IconButton 
                    onClick={handleCloseReport} 
                    sx={{
                      color: '#666666',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        color: '#333333'
                      }
                    }}
                    title={t('Close')}
                  >
                    <MuiCloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Tab Navigation */}
              <Box sx={{
                display: 'flex',
                borderBottom: '1px solid #ddd',
                marginBottom: '10px'
              }}>
                {[
                  { key: 'all', label: t('All') + ' (כל המסמכים)', color: '#304FFF' },
                  { key: 'taxInvoices', label: t('TaxInvoice'), color: '#1976d2' },
                  { key: 'creditInvoices', label: t('CreditNote') , color: '#d32f2f' },
                  { key: 'invoiceReceipts', label: t('InvoiceReceipt') , color: '#388e3c' }
                ].map((tab) => (
                  <Box
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    sx={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
                      backgroundColor: activeTab === tab.key ? '#f5f5f5' : 'transparent',
                      color: activeTab === tab.key ? tab.color : '#666',
                      fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                        color: tab.color
                      }
                    }}
                  >
                    {tab.label}
                  </Box>
                ))}
              </Box>

              {/* Tab Content */}
              <Box sx={{
                height: 'calc(100% - 120px)',
                overflow: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                <div
                  dangerouslySetInnerHTML={{ 
                    __html: tabContent[activeTab] || htmlContent 
                  }}
                  onClick={(event) => {
                    const row = event.target.closest('tr[data-income-id]');
                    if (row) {
                      const incomeId = row.getAttribute('data-income-id');
                      const documentType = row.getAttribute('data-document-type') || 'Incomes';
                      if (incomeId) {
                        handleIncomeClick(incomeId, documentType);
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <strong style={{
                color: "var(--black-90, rgba(0, 0, 0, 0.90))",
                textAlign: "center",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "normal",
              }}>{t('ViewReferenceSequenceReport')}</strong>

              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'nowrap',
                marginTop: '12px',
              }}>
                <CustomYearDatePicker
                  date={startYear}
                  onDateChange={(newYear) => {
                    setStartYear(newYear);
                    if (dayjs(newYear).year() > dayjs(endYear).year()) {
                      setEndYear(newYear);
                    }
                  }}
                  labelMonth={t('StartYear')}
                />
                <CustomYearDatePicker
                  date={endYear}
                  onDateChange={(newYear) => {
                    setEndYear(newYear);
                    if (dayjs(newYear).year() < dayjs(startYear).year()) {
                      setStartYear(newYear);
                    }
                  }}
                  labelMonth={t('EndYear')}
                />
              </div>

              <div style={{ width: '80%', margin: '20px auto' }}>
                <AutocompleteSelect
                  options={formattedAccountCardOptions}
                  selectedValues={selectedFormattedAccountCard}
                  onChange={handleFormattedAccountCardChange}
                  label={t("Select AccountCard")}
                  placeholder={t("Select AccountCard")}
                  isMultiple={false}
                  width='100%'
                />
              </div>

              <div style={{ marginTop: '10px', width: '80%', margin: '10px auto 0 auto' }}>
                <AutocompleteSelect
                  options={sortCodeOptions}
                  selectedValues={selectedSortCode}
                  onChange={(event, newValue) => {
                    setSelectedSortCode(newValue);
                  }}
                  renderInput={(params) => {
                    const inputPropsStyle = {
                      disableUnderline: true,
                      className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                      sx: {
                        height: '48px',
                        padding: '0 10px',
                        borderRadius: '8px',
                        background: '#F5F5F6',

                      },
                    };

                    const inputStyle = {
                      style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                    };

                    return (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={t("Select Sort Code")}
                        placeholder={t("Select Sort Code")}
                        InputProps={{
                          ...params.InputProps,
                          ...inputPropsStyle,
                        }}
                        inputProps={{
                          ...params.inputProps,
                          ...inputStyle,
                        }}
                      />
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.sortCodeId === value?.sortCodeId}
                  PopperComponent={(props) => (
                    <StyledPopper
                      {...props}
                      direction={direction}
                      sx={{
                        direction: direction === 'rtl' ? 'rtl' : 'ltr',
                        '& .MuiPaper-root': {
                          borderRadius: 'var(--Spacing-md, 8px)',
                          background: 'var(--white, #FFF)',
                          boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
                        },
                        '& .MuiAutocomplete-listbox': {
                          padding: 1,
                        },
                        '& .MuiAutocomplete-option': {
                          borderRadius: 'var(--Spacing-sm, 4px)',
                          textAlign: direction === 'ltr' ? 'left' : 'right',
                          direction: direction,
                          justifyContent: direction === 'ltr' ? 'flex-start' : 'flex-end',
                          display: 'flex',
                          '&[aria-selected="true"], &.Mui-focused, &:hover': {
                            background: 'var(--orange, #F9AA2A) !important',
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    width: '100%',
                    minWidth: '100%',
                  }}
                  label={t("Select Sort Code")}
                  placeholder={t("Select Sort Code")}
                  isMultiple={false}
                  width='100%'
                  direction={direction}
                />
              </div>

              {error && (
                <Alert severity="error" sx={{ marginTop: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleViewReport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <MuiVisibilityIcon sx={{ ml: 1 }} />}
                sx={{
                  width: '80%', height: '44px', mt: 1, backgroundColor: '#304FFF',
                  borderRadius: '8px', boxShadow: "0px 6px 6px 0px rgba(0, 0, 0, 0.08)",
                  color: '#fff', '&:hover': { backgroundColor: '#1565c0' }
                }}
              >
                {loading ? t('Loading') : t('ViewReport')}
              </Button>
            </>
          )}
        </Box>
      </Modal>

      

    </>
  );
};

ReferenceSequenceViewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  direction: PropTypes.string,
  t: PropTypes.func.isRequired,
  CustomYearDatePicker: PropTypes.elementType.isRequired,
  startYearDateForReference: PropTypes.any,
  handleStartReferenceYearDate: PropTypes.func.isRequired,
  dayjs: PropTypes.func.isRequired,
  formattedAccountCardOptions: PropTypes.array,
  accountCardOptions: PropTypes.array,
  selectedFormattedAccountCard: PropTypes.any,
  handleFormattedAccountCardChange: PropTypes.func.isRequired,
  AutocompleteSelect: PropTypes.elementType.isRequired,
  client: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired
};

export default ReferenceSequenceViewModal;
